export function replace<T>(
  array: Array<T>,
  newElement: T,
  criteria: string | Array<string> = 'id',
): T[] {
  return array.map((item) => {
    if (
      (typeof criteria === 'string' &&
        item[criteria] === newElement[criteria]) ||
      (Array.isArray(criteria) &&
        criteria.every((key) => item[key] === newElement[key]))
    ) {
      return newElement;
    }

    return item;
  });
}

type SortOrder = 'asc' | 'desc';

export function orderBy<T>(
  array: Array<T>,
  keys: string | string[],
  order: SortOrder | SortOrder[] = 'asc',
): T[] {
  if (!array?.length) return [];

  const keysArray = Array.isArray(keys) ? keys : [keys];
  const orderArray = Array.isArray(order) ? order : [order];

  const getValueByKey = (obj: T, key: string) => {
    return key.split('.').reduce((acc, part) => acc && acc[part], obj);
  };

  const compare = (a: T, b: T, index = 0): number => {
    if (index >= keysArray.length) return 0;

    const key = keysArray[index];
    const sortOrder = orderArray[index] === 'desc' ? -1 : 1;

    const aValue = getValueByKey(a, key);
    const bValue = getValueByKey(b, key);

    if (aValue < bValue) return -1 * sortOrder;
    if (aValue > bValue) return 1 * sortOrder;

    return compare(a, b, index + 1);
  };

  return array.slice().sort((a, b) => compare(a, b));
}

export function groupBy<T>(
  array: Array<T>,
  key: string,
): Record<string | number, T[]> {
  return array.reduce(
    (result, item) => {
      const groupKey = item[key as keyof T];

      if (!result[groupKey as string | number]) {
        result[groupKey as string | number] = [];
      }

      result[groupKey as string | number].push(item);

      return result;
    },
    {} as Record<string | number, T[]>,
  );
}

export function uniqBy<T>(array: T[], key: string | number = 'id') {
  const seen = new Map();
  return array.filter((item) => {
    if (seen.has(item[key])) {
      return false;
    }
    seen.set(item[key], true);

    return true;
  });
}

export function minBy<T>(
  array: T[],
  iteratee: ((item: T) => number) | keyof T,
): T | undefined {
  if (!array || array.length === 0) return undefined;

  let minItem: T | undefined = undefined;
  let minValue: number | undefined = undefined;

  for (const item of array) {
    const value =
      typeof iteratee === 'function'
        ? iteratee(item)
        : (item[iteratee] as unknown as number);

    if (minValue === undefined || value < minValue) {
      minValue = value;
      minItem = item;
    }
  }

  return minItem;
}

export function isEqual<T>(a: T, b: T): boolean {
  if (a === b) return true;

  if (
    a === null ||
    b === null ||
    typeof a !== 'object' ||
    typeof b !== 'object'
  ) {
    return false;
  }

  if (Array.isArray(a) !== Array.isArray(b)) return false;

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
    if (!isEqual(a[key], b[key])) return false;
  }

  return true;
}

export function deepClone(obj: any) {
  return JSON.parse(JSON.stringify(obj));
}
